import React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import { Button, Type, Icon } from '@americanexpress/aqx-components';
import { makeGetCardData, getUpgradeCta } from './redux';
import { createCombinedApplyHandler } from 'src/universal/utils/applyOfferGatingHandler';
import { getActivePageName } from 'src/universal/redux/page';
import { pznPageIds } from 'src/universal/components/PZN/config';
import { mapUrlWithDtmLinkQuery } from 'service_modules/models/dtm-tagging';
import { isG171Experiment } from 'src/universal/components/Experiments/G171/redux';
import removeTags from 'service_modules/utils/text-remove-tags';

import styles from './__styles__/index.scss?modules';
import LazyScrollMetricTracker from '../DTM/LazyScrollMetricTracker';

/*
US5160057:  A long overdue corrective refactoring as follows:
(1) If the incoming allowUpgradeCta is true, and
    If the user is a CM and his/her card is eligible for an upgrade, it
    produces 2 CTAs: Upgrade cta and Apply cta.
(2) Otherwise, it produces only one CTA: the Apply cta.
*/
const ApplyOrUpgradeCta = ({
  allowUpgradeCta = false,
  upgradeCta,
  cardData,
  pznPageId,
  position = '',
  isG171,
  makeApplySecondaryCta = false
}) => {
  if (!cardData?.pznArrived) return <div className={styles.placeholder} />;

  const { needUpgradeCta, upgradeBtn } = upgradeCta;
  const renderUpgradeCta = allowUpgradeCta && needUpgradeCta;
  const applyButton = {
    ...(cardData?.links?.apply || {})
  };

  const makeDtm = ctaType => {
    let assetVal = `${ctaType}_${cardData.dtmProductName}`;
    if (position) {
      assetVal += `_${position}`;
    }
    return {
      eventAction: 'click',
      eventType: 'pageInteraction',
      type: 'intlink',
      eventAsset: assetVal,
      tag: assetVal
    };
  };

  const dtmApply = makeDtm('Apply');
  const dtmUpgrade = makeDtm('Upgrade');
  applyButton.url = mapUrlWithDtmLinkQuery(applyButton.url, dtmApply);
  applyButton.dtm = dtmApply;
  upgradeBtn.dtm = dtmUpgrade;

  const clsApply =
    renderUpgradeCta || makeApplySecondaryCta ? styles.ctaSecondary : styles.button;

  const impressionDtm = {
    ...applyButton.dtm,
    eventAction: 'impression'
  };

  return (
    <React.Fragment>
      <LazyScrollMetricTracker
        scrollDtm={impressionDtm}
        startTrackingAfterPznArrival={true}
      />
      {renderUpgradeCta && (
        <Button
          Component="button"
          className={clsx(styles.button, styles.isUpgrade, {
            [styles.hasLock]: !isG171
          })}
          $type={Type.Primary}
          title={upgradeBtn.text}
          href={upgradeBtn.url}
          ariaLabel={removeTags(`${upgradeBtn.text} for ${cardData?.cardTitle}`)}
        >
          {!isG171 && (
            <Icon
              directory="dls"
              imageName="dls-icon-lock"
              className={styles.icon}
            />
          )}
          <p>{upgradeBtn.text}</p>
        </Button>
      )}
      <Button
        Component="button"
        className={clsx(clsApply, { [styles.hasLock]: !isG171 })}
        $type={
          renderUpgradeCta || makeApplySecondaryCta ? Type.Secondary : Type.Primary
        }
        title={applyButton.text}
        href={applyButton.url}
        ariaLabel={removeTags(`${applyButton.text} for ${cardData?.cardTitle}`)}
        onClick={createCombinedApplyHandler({
          linkDef: applyButton,
          card: cardData,
          pagePznId: pznPageId,
          cmPznEventInfo: upgradeCta.cmPznEventInfo,
          modalName: `apply_${cardData.shortName}`
        })}
      >
        {!isG171 && (
          <Icon directory="dls" imageName="dls-icon-lock" className={styles.icon} />
        )}
        <p>{applyButton.text}</p>
      </Button>
    </React.Fragment>
  );
};

const mapStateToProps = () => {
  const getCardData = makeGetCardData();
  return (state, { shortName }) => {
    const pageName = getActivePageName(state);

    return {
      cardData: getCardData(state, shortName),
      upgradeCta: getUpgradeCta(state, shortName),
      pznPageId: pznPageIds[pageName],
      isG171: isG171Experiment(state)
    };
  };
};

export default connect(mapStateToProps)(ApplyOrUpgradeCta);
